import { useEffect, useState } from "react";
import ShineMark from "./ShineMark";
import { RULEBOOK } from "../lib/rulebook";

/**
 * Rulebook download feedback (client, 2026-09-09).
 *
 * Every `<a download>` that points at the rulebook - hero, nav sub-link, menu,
 * Rules & Regulations section, entry page - is intercepted here in ONE place.
 * While the PDF is being fetched the site stays visible behind a blurred black
 * veil with the Grid Masters shield and its light sweep (client, 2026-09-19;
 * was the pulse); the moment the file is in hand it
 * is handed to the browser's download and the veil lifts. If the fetch fails
 * for any reason we fall back to the plain link so the download still happens.
 */
const MIN_SHOW_MS = 450;

export default function DownloadOverlay() {
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = false;
    const onClick = (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target.closest?.("a[download]");
      if (!a) return;
      const href = a.getAttribute("href") || "";
      if (!href.endsWith(RULEBOOK.url)) return;
      if (active) { e.preventDefault(); return; }
      e.preventDefault();
      active = true;
      setBusy(true);
      const filename = a.getAttribute("download") || RULEBOOK.filename;
      const started = Date.now();
      const done = () => {
        const wait = Math.max(0, MIN_SHOW_MS - (Date.now() - started));
        setTimeout(() => { active = false; setBusy(false); }, wait);
      };
      (async () => {
        try {
          const res = await fetch(href, { credentials: "same-origin" });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          const tmp = document.createElement("a");
          tmp.href = url;
          tmp.download = filename;
          tmp.rel = "noopener";
          document.body.appendChild(tmp);
          tmp.click();
          tmp.remove();
          setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch {
          // Fall back to the browser's native handling of the original link.
          const tmp = document.createElement("a");
          tmp.href = href;
          tmp.download = filename;
          document.body.appendChild(tmp);
          tmp.click();
          tmp.remove();
        } finally {
          done();
        }
      })();
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  if (!busy) return null;
  return (
    <div className="gm-dl" role="status" aria-live="polite" aria-label="Preparing your download" data-testid="download-overlay">
      <ShineMark size={200} className="gm-dl__mark" testid="download-overlay-mark" />
    </div>
  );
}
